import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  TextField,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIosNewSharpIcon from '@mui/icons-material/ArrowBackIosNewSharp';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import QueryString from 'qs';
import moment from 'moment';
import { getEmployeeAttendHistories } from '../../../api/staff';
import { FormInputText } from '../../../form-components/FormInputText';
import AnimationPage from '../../../pages/AnimationPage';
import { errorMessages } from '../../../utils/methods';
import { CommonExcelButton } from '../../../form-components/CommonExcelButton';
import CommonSearchSubmitButton from '../../../form-components/CommonSearchSubmitButton';
import useUserAuth from '../../../utils/useUserAuth';
import {
  PageContainer,
  HeaderRow,
  PageTitle,
  SearchSection,
  FilterSection,
  FilterItem,
  FilterLabel,
  ResetButton,
  TodayButton,
  MonthNavButton,
  TableWrapper,
  SummaryCard,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  StatusChip,
  PaginationWrapper,
  PaginationStyles,
  Divider,
  EmptyState,
  EmptyStateText,
} from './AttendanceManagementStyled';

// 기간 타입 상수
const PERIOD_TYPES = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  CUSTOM: 'CUSTOM',
};

const AttendanceManagement = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const canView = useUserAuth('EMPLOYEE_ATTENDANCE_VIEW');
  const token = user?.token;
  const userBranch = user?.branch;
  const userHead = user?.headBranch;

  // 기본값: 당일
  const initialQuery = {
    page: 1,
    limit: 10,
    headBranchId: userHead?.headBranchId,
    branchId: userBranch?.branchId,
    searchInput: '',
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  };

  const [loading, setLoading] = useState(false);
  const [attendanceList, setAttendanceList] = useState({ content: [], totalElements: 0 });
  const [query, setQuery] = useState(initialQuery);
  const [periodType, setPeriodType] = useState(PERIOD_TYPES.DAY);
  const [summary, setSummary] = useState({
    totalRecords: 0,
    totalComplete: 0,
    totalWorking: 0,
  });

  const { handleSubmit, control, reset, setValue } = useForm({
    defaultValues: initialQuery,
  });

  // 출퇴근 기록 조회
  const fetchAttendanceList = async () => {
    const branchId = userBranch?.branchId;
    if (!user.headBranch?.headBranchId || (user.centerType === 'BRANCH' && !branchId)) {
      toast.warning(t('Please select a point'));
      setAttendanceList({ content: [], totalElements: 0 });
      return;
    }

    try {
      setLoading(true);
      const queryParams = {
        branchId,
        startDate: query.startDate,
        endDate: query.endDate,
        searchInput: query.searchInput,
        page: query.page,
        limit: query.limit,
      };
      const queryString = QueryString.stringify(queryParams);
      const result = await getEmployeeAttendHistories(token, queryString);
      setAttendanceList(result || { content: [], totalElements: 0 });

      // 요약 계산
      const content = result?.content || [];
      const summaryData = content.reduce(
        (acc, item) => {
          acc.totalRecords += 1;
          if (item.entryCount > 0 && item.exitCount > 0) {
            acc.totalComplete += 1; // 퇴근 완료
          } else if (item.entryCount > 0 && item.exitCount === 0) {
            acc.totalWorking += 1; // 근무 중
          }
          return acc;
        },
        { totalRecords: 0, totalComplete: 0, totalWorking: 0 },
      );
      setSummary(summaryData);
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setLoading(false);
    }
  };

  // 검색 제출
  const handleSearchSubmit = values => {
    setQuery({
      ...query,
      ...values,
      page: 1,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
    });
  };

  // 페이지 변경
  const handleChangePage = (_, value) => {
    setQuery({ ...query, page: value });
  };

  // 검색 조건 초기화
  const handleReset = () => {
    const resetQuery = {
      ...initialQuery,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
    };
    setQuery(resetQuery);
    reset(resetQuery);
    setPeriodType(PERIOD_TYPES.DAY);
  };

  // 기간 타입 변경 핸들러
  const handlePeriodTypeChange = event => {
    const newType = event.target.value;
    setPeriodType(newType);

    let newStartDate, newEndDate;

    switch (newType) {
      case PERIOD_TYPES.DAY:
        newStartDate = moment().format('YYYY-MM-DD');
        newEndDate = moment().format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.WEEK:
        newStartDate = moment().startOf('week').format('YYYY-MM-DD');
        newEndDate = moment().endOf('week').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.MONTH:
        newStartDate = moment().startOf('month').format('YYYY-MM-DD');
        newEndDate = moment().endOf('month').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.CUSTOM:
        // 직접 선택 시 현재 값 유지
        return;
      default:
        return;
    }

    const newQuery = { ...query, page: 1, startDate: newStartDate, endDate: newEndDate };
    setQuery(newQuery);
    setValue('startDate', newStartDate);
    setValue('endDate', newEndDate);
  };

  // 오늘 버튼
  const handleToday = () => {
    const todayQuery = {
      ...query,
      page: 1,
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    };
    setQuery(todayQuery);
    setValue('startDate', todayQuery.startDate);
    setValue('endDate', todayQuery.endDate);
    setPeriodType(PERIOD_TYPES.DAY);
  };

  // 이전 기간
  const handlePrev = () => {
    let newStartDate, newEndDate;

    switch (periodType) {
      case PERIOD_TYPES.DAY:
        newStartDate = moment(query.startDate).subtract(1, 'day').format('YYYY-MM-DD');
        newEndDate = moment(query.endDate).subtract(1, 'day').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.WEEK:
        newStartDate = moment(query.startDate).subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
        newEndDate = moment(query.startDate).subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.MONTH:
        newStartDate = moment(query.startDate).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        newEndDate = moment(query.startDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      default:
        // CUSTOM일 경우 기간 길이만큼 이전으로 이동
        const daysDiff = moment(query.endDate).diff(moment(query.startDate), 'days') + 1;
        newStartDate = moment(query.startDate).subtract(daysDiff, 'days').format('YYYY-MM-DD');
        newEndDate = moment(query.endDate).subtract(daysDiff, 'days').format('YYYY-MM-DD');
    }

    const newQuery = { ...query, page: 1, startDate: newStartDate, endDate: newEndDate };
    setQuery(newQuery);
    setValue('startDate', newStartDate);
    setValue('endDate', newEndDate);
  };

  // 다음 기간
  const handleNext = () => {
    let newStartDate, newEndDate;

    switch (periodType) {
      case PERIOD_TYPES.DAY:
        newStartDate = moment(query.startDate).add(1, 'day').format('YYYY-MM-DD');
        newEndDate = moment(query.endDate).add(1, 'day').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.WEEK:
        newStartDate = moment(query.startDate).add(1, 'week').startOf('week').format('YYYY-MM-DD');
        newEndDate = moment(query.startDate).add(1, 'week').endOf('week').format('YYYY-MM-DD');
        break;
      case PERIOD_TYPES.MONTH:
        newStartDate = moment(query.startDate).add(1, 'month').startOf('month').format('YYYY-MM-DD');
        newEndDate = moment(query.startDate).add(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      default:
        // CUSTOM일 경우 기간 길이만큼 다음으로 이동
        const daysDiff = moment(query.endDate).diff(moment(query.startDate), 'days') + 1;
        newStartDate = moment(query.startDate).add(daysDiff, 'days').format('YYYY-MM-DD');
        newEndDate = moment(query.endDate).add(daysDiff, 'days').format('YYYY-MM-DD');
    }

    const newQuery = { ...query, page: 1, startDate: newStartDate, endDate: newEndDate };
    setQuery(newQuery);
    setValue('startDate', newStartDate);
    setValue('endDate', newEndDate);
  };

  // 날짜 표시 포맷
  const getDateDisplayText = () => {
    const start = moment(query.startDate);
    const end = moment(query.endDate);

    switch (periodType) {
      case PERIOD_TYPES.DAY:
        return start.format('YYYY년 MM월 DD일 (ddd)');
      case PERIOD_TYPES.WEEK:
        return `${start.format('YYYY년 MM월 DD일')} ~ ${end.format('MM월 DD일')}`;
      case PERIOD_TYPES.MONTH:
        return start.format('YYYY년 MM월');
      default:
        return `${start.format('YYYY-MM-DD')} ~ ${end.format('YYYY-MM-DD')}`;
    }
  };

  // user 변경 시 query 업데이트
  useEffect(() => {
    setQuery(prevQuery => ({
      ...prevQuery,
      headBranchId: userHead?.headBranchId,
      branchId: userBranch?.branchId,
    }));
  }, [user, userHead?.headBranchId, userBranch?.branchId]);

  // query 변경 시 목록 조회
  useEffect(() => {
    fetchAttendanceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const totalPages = Math.ceil(attendanceList?.totalElements / query.limit) || 0;

  // 상태 텍스트 변환
  const getStatusText = status => {
    switch (status) {
      case 'ATTENDED':
        return t('출근');
      case 'LATE':
        return t('지각');
      case 'ABSENT':
        return t('결근');
      case 'EARLY_LEAVE':
        return t('조퇴');
      default:
        return status || '-';
    }
  };

  // 시간 포맷팅 (HH:mm:ss -> HH:mm)
  const formatTime = time => {
    if (!time) return '-';
    // 이미 HH:mm:ss 형태의 문자열이면 HH:mm만 추출
    if (typeof time === 'string' && time.includes(':')) {
      return time.substring(0, 5);
    }
    return moment(time).format('HH:mm');
  };

  // 날짜 포맷팅
  const formatDate = date => {
    if (!date) return '-';
    return moment(date).format('YYYY-MM-DD (ddd)');
  };

  // 근무 시간 계산
  const calculateWorkingHours = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    const start = moment(startTime, 'HH:mm:ss');
    const end = moment(endTime, 'HH:mm:ss');
    const duration = moment.duration(end.diff(start));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    if (hours > 0 && minutes > 0) {
      return `${hours}시간 ${minutes}분`;
    } else if (hours > 0) {
      return `${hours}시간`;
    } else if (minutes > 0) {
      return `${minutes}분`;
    }
    return '-';
  };

  // 출퇴근 상태 계산
  const getAttendStatus = row => {
    if (row.entryCount > 0 && row.exitCount > 0) {
      return 'COMPLETE'; // 출퇴근 완료
    } else if (row.entryCount > 0 && row.exitCount === 0) {
      return 'WORKING'; // 근무 중
    } else {
      return 'NONE';
    }
  };

  // 상태 텍스트
  const getStatusTextByAttend = row => {
    const status = getAttendStatus(row);
    switch (status) {
      case 'COMPLETE':
        return t('퇴근 완료');
      case 'WORKING':
        return t('근무 중');
      default:
        return '-';
    }
  };

  if (!canView) {
    return (
      <AnimationPage>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', color: '#999', fontSize: '16px' }}>
          조회 권한이 없습니다.
        </Box>
      </AnimationPage>
    );
  }

  return (
    <AnimationPage>
      <PageContainer>
        {/* Header */}
        <HeaderRow>
          <SearchSection>
            <PageTitle>{t('Attendance management')}</PageTitle>
            <FormInputText
              control={control}
              sx={{ width: '300px' }}
              name="searchInput"
              placeholder={t('Search by your employees name and phone number')}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSubmit(handleSearchSubmit)();
                }
              }}
            />
            <CommonSearchSubmitButton variant="primary" onClick={handleSubmit(handleSearchSubmit)}>
              {t('Search')}
            </CommonSearchSubmitButton>
          </SearchSection>
          <CommonExcelButton onClick={() => toast.info(t('준비중입니다'))}>
            {t('Download Excel File')}
          </CommonExcelButton>
        </HeaderRow>

        <Divider />

        {/* 날짜 필터 */}
        <FilterSection>
          <TodayButton onClick={handleToday}>{t('오늘')}</TodayButton>
          <MonthNavButton onClick={handlePrev}>
            <ArrowBackIosNewSharpIcon sx={{ fontSize: 14 }} />
          </MonthNavButton>
          <PageTitle sx={{ margin: '0 8px', fontSize: '14px', minWidth: '200px', textAlign: 'center' }}>
            {getDateDisplayText()}
          </PageTitle>
          <MonthNavButton onClick={handleNext}>
            <ArrowForwardIosSharpIcon sx={{ fontSize: 14 }} />
          </MonthNavButton>

          <FilterItem sx={{ marginLeft: '20px' }}>
            <FilterLabel>{t('조회 기간')}</FilterLabel>
            <Select
              value={periodType}
              onChange={handlePeriodTypeChange}
              size="small"
              sx={{ minWidth: '120px', height: '40px' }}>
              <MenuItem value={PERIOD_TYPES.DAY}>{t('일별')}</MenuItem>
              <MenuItem value={PERIOD_TYPES.WEEK}>{t('주별')}</MenuItem>
              <MenuItem value={PERIOD_TYPES.MONTH}>{t('월별')}</MenuItem>
              <MenuItem value={PERIOD_TYPES.CUSTOM}>{t('직접 선택')}</MenuItem>
            </Select>
          </FilterItem>

          {periodType === PERIOD_TYPES.CUSTOM && (
            <>
              <FilterItem>
                <FilterLabel>{t('Start date')}</FilterLabel>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      size="small"
                      sx={{ width: '150px' }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </FilterItem>

              <FilterItem>
                <FilterLabel>{t('End Date')}</FilterLabel>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      size="small"
                      sx={{ width: '150px' }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </FilterItem>

              <CommonSearchSubmitButton variant="primary" onClick={handleSubmit(handleSearchSubmit)}>
                {t('Apply and Search')}
              </CommonSearchSubmitButton>
            </>
          )}

          <ResetButton variant="outlined" onClick={handleReset}>
            {t('Clear Filters')}
          </ResetButton>
        </FilterSection>

        {/* 요약 카드 */}
        <SummaryCard>
          <SummaryItem>
            <SummaryLabel>{t('Total records')}</SummaryLabel>
            <SummaryValue>{attendanceList?.totalElements || 0}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{t('퇴근 완료')}</SummaryLabel>
            <SummaryValue sx={{ color: '#2e7d32' }}>{summary.totalComplete}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{t('근무 중')}</SummaryLabel>
            <SummaryValue sx={{ color: '#ef6c00' }}>{summary.totalWorking}</SummaryValue>
          </SummaryItem>
        </SummaryCard>

        {/* 테이블 */}
        <TableWrapper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ background: '#fafbfc' }}>
                <TableRow>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('Date')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('Employee Name')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('연락처')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('출근 시간')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('퇴근 시간')}
                  </TableCell>
                  <TableCell align="center" sx={{ borderRight: '1px solid #ddd', fontWeight: 600 }}>
                    {t('근무 시간')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {t('Status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceList?.content?.length > 0 ? (
                  attendanceList.content.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      sx={{
                        height: '48px',
                        '&:hover': { background: '#f5f5f5' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {formatDate(row.writeDate)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {row.employeeName || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {row.phoneNumber || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {formatTime(row.startTime)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {formatTime(row.endTime)}
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                        {calculateWorkingHours(row.startTime, row.endTime)}
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip status={getAttendStatus(row)}>{getStatusTextByAttend(row)}</StatusChip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <EmptyState>
                        <EventNoteOutlinedIcon sx={{ fontSize: 48, color: '#ccc' }} />
                        <EmptyStateText>{t('No attendance records found')}</EmptyStateText>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableWrapper>

        {/* 페이지네이션 */}
        {totalPages > 0 && (
          <PaginationWrapper>
            <Stack spacing={1}>
              <Pagination
                count={totalPages}
                onChange={handleChangePage}
                page={query.page}
                color="primary"
                variant="outlined"
                shape="rounded"
                sx={PaginationStyles}
              />
            </Stack>
          </PaginationWrapper>
        )}
      </PageContainer>
    </AnimationPage>
  );
};

export default AttendanceManagement;
